/**
 * BrandWordmark - The "GBRANDS" logotype, rendered consistently everywhere.
 *
 * The wordmark is always a single word with a brand-orange leading "G" and the
 * remaining letters in a solid tone. Before this atom existed the markup was
 * copy-pasted into the sidebar, top bar, nav bar, footer and the two auth
 * screens, which is exactly how the nav bar drifted to an all-orange logo.
 *
 * Tone controls the color of the "BRANDS" letters:
 *	 - 'dark'  (default) — black letters, for light backgrounds (app chrome, auth)
 *	 - 'light'           — white letters, for dark backgrounds (the footer)
 *
 * The brand name itself is data, not markup: change BRAND_NAME (or pass `name`)
 * and every surface follows, which keeps the white-label story intact.
 *
 * @example
 * <BrandWordmark size="20px" />
 * <BrandWordmark tone="light" size="20px" className="mb-12px" />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Which background the wordmark sits on — drives the letter color */
export type BrandWordmarkTone = 'dark' | 'light';

/** Size presets — kept as named tokens so the Tailwind classes stay static */
export type BrandWordmarkSize = 'sm' | 'md' | 'lg' | 'xl';

export interface BrandWordmarkProps {
	/** Letter color for everything after the leading character (default: 'dark') */
	tone?: BrandWordmarkTone;
	/** Font size preset — maps to a static Tailwind text-*px utility (default: 'md') */
	size?: BrandWordmarkSize;
	/** Full brand name — the first character is always the accented one */
	name?: string;
	/** Optional extra classes merged after the base classes */
	className?: string;
	/** Optional heading level when the wordmark acts as a page/section heading */
	as?: 'span' | 'h1' | 'h3';
}

/* ============================================
 * CONSTANTS
 * ============================================ */

/** The brand name — first letter renders in brand orange, the rest in `tone` */
const BRAND_NAME = 'GBRANDS';

/**
 * The company tagline, shown under the wordmark in the sidebar. Lives beside
 * BRAND_NAME so re-branding is one file. It replaced a "WORK ORDERS"
 * subtitle (beta feedback, Oct 2026: "It should not say 'Work Orders' under
 * G Brands logo. Either our tag line or nothing please.").
 */
export const BRAND_TAGLINE = 'Cleaning and Creating Solutions';

/** Letter color per tone. Dark = black on light chrome, light = white on the footer. */
const TONE_CLASSES: Record<BrandWordmarkTone, string> = {
	dark: 'text-black',
	light: 'text-white',
};

/**
 * Size presets. These are written out as complete literal class strings (never
 * interpolated) so Tailwind's JIT scanner can actually see and emit them.
 */
const SIZE_CLASSES: Record<BrandWordmarkSize, string> = {
	sm: 'text-18px',						/* mobile top bar */
	md: 'text-20px',						/* sidebar + footer */
	lg: 'text-24px',						/* marketing nav bar */
	xl: 'text-30px tablet:text-36px',		/* auth screens */
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const BrandWordmark: React.FC<BrandWordmarkProps> = ({
	tone = 'dark',
	size = 'md',
	name = BRAND_NAME,
	className = '',
	as: Tag = 'span',
}) => {
	// Split into the accented leading character and the remainder so the
	// orange "G" never has to be hand-written by callers again. (slice/charAt
	// rather than array destructuring — the build targets ES5.)
	const lead = name.charAt(0);
	const rest = name.slice(1);

	return (
		<Tag
			className={`brand-wordmark font-heading ${SIZE_CLASSES[size]} tracking-wider leading-tight m-0 ${TONE_CLASSES[tone]} ${className}`}
			data-ref="atom-brand-wordmark"
		>
			{/* Leading letter always carries the brand primary color */}
			<span className="brand-wordmark__lead text-brand-primary">{lead}</span>
			{rest}
		</Tag>
	);
};

export default BrandWordmark;
